/**
 * Seating write tools — assign/move/unassign guests and manage tables by
 * reusing the Firestore helpers in features/seating/operations.ts.
 */

import { Schema } from "firebase/ai";
import { type Guest, type Table, type TableShape } from "@amazing-wedding/shared-types";
import { VIP_TABLE_CAPACITY } from "../seating/constants";
import { defaultTableName, defaultVipTableName } from "../seating/seating-defaults";
import {
  canPlaceParty,
  effectiveSeatMap,
  parseOccupantKey,
  removeGuestFromSeatMap,
} from "../seating/seat-map";
import { isGuestShownAsCompanion } from "../guests/guest-unit-rows";
import type { PlannerTool } from "./registry";
import type { ToolContext } from "../types";
import { asBoolean, asEnum, asNumber, asString, requireNumber, requireString } from "./helpers";
import {
  defaultTablePosition,
  findFirstEmptySeat,
  findGuestSeatIndex,
  guestDisplayName,
  guestName,
  guestTableInPlan,
  guestsById,
  requireSeatingPlanId,
  seatNumberToIndex,
  tableHeadCount,
  tableName,
} from "../seating/seatingToolHelpers";

const TABLE_SHAPES = ["round", "rectangular", "square", "oval"] as const;

function resolveGuest(ctx: ToolContext, guestId: string): Guest {
  const guest = ctx.guests().find((g) => g.id === guestId);
  if (!guest) {
    throw new Error(`No guest with ID "${guestId}". Call list_guests first.`);
  }
  return guest;
}

function resolveTable(ctx: ToolContext, tableId: string): Table {
  const table = ctx.tables().find((t) => t.id === tableId);
  if (!table) {
    throw new Error(`No table with ID "${tableId}". Call list_tables first.`);
  }
  return table;
}

function displacedGuestName(
  seatMap: (string | null)[],
  targetSeatIndex: number,
  movingGuestId: string,
  byId: ReturnType<typeof guestsById>,
): string | null {
  const occupiedKey = seatMap[targetSeatIndex];
  if (!occupiedKey) return null;
  const displacedId = parseOccupantKey(occupiedKey).guestId;
  if (displacedId === movingGuestId) return null;
  const displaced = byId[displacedId];
  return displaced ? `${displaced.firstName} ${displaced.lastName}`.trim() : displacedId;
}

export const seatingTools: PlannerTool[] = [
  {
    declaration: {
      name: "setup_seating_plan",
      description:
        "Create tables AND assign all guests in one step. Use when the couple asks to generate, create or distribute a full seating layout (especially on an empty plan). Creates guest tables sized for the guest list (round by default, or rectangular when requested), seats every guest (ignores RSVP status), and keeps guest groups together when possible. Prefer this over calling create_table many times without assign_guest_to_seat.",
      parameters: Schema.object({
        properties: {
          capacity: Schema.number({
            description: "Seats per guest table (2–30). Defaults to 8.",
          }),
          tableCount: Schema.number({
            description:
              "Number of guest tables. Omit to auto-calculate from guest head count and capacity.",
          }),
          shape: Schema.enumString({
            enum: ["round", "rectangular"],
            description:
              "Shape of the generated guest tables. Defaults to 'round'. Use 'rectangular' when the couple asks for long / banquet-style tables.",
          }),
          includeVipTable: Schema.boolean({
            description: `Also add a rectangular VIP head table (${VIP_TABLE_CAPACITY} seats) for the couple.`,
          }),
          assignGuests: Schema.boolean({
            description: "When false, create tables only without seating guests. Defaults to true.",
          }),
          keepGroupsTogether: Schema.boolean({
            description:
              "When assigning, prefer keeping guest groups at the same table. Defaults to true.",
          }),
          leaveSpareSeats: Schema.boolean({
            description: "When assigning, leave about one spare seat per table. Defaults to false.",
          }),
        },
        optionalProperties: [
          "capacity",
          "tableCount",
          "shape",
          "includeVipTable",
          "assignGuests",
          "keepGroupsTogether",
          "leaveSpareSeats",
        ],
      }),
    },
    destructive: false,
    describe: (args, ctx) => {
      const cap = asNumber(args.capacity) ?? 8;
      const count = asNumber(args.tableCount);
      const vip = asBoolean(args.includeVipTable) ?? true;
      const assign = asBoolean(args.assignGuests) ?? true;
      const tablesPart =
        count != null
          ? ctx.translate("seating.aiTools.setupPlanTablesCount", { count, capacity: cap })
          : ctx.translate("seating.aiTools.setupPlanTablesAuto", { capacity: cap });
      const assignPart = assign
        ? ctx.translate("seating.aiTools.setupPlanAssignOn")
        : ctx.translate("seating.aiTools.setupPlanAssignOff");
      return ctx.translate("seating.aiTools.setupPlan", {
        tables: tablesPart,
        vipSuffix: vip ? ctx.translate("seating.aiTools.setupPlanVipSuffix") : "",
        assignMode: assignPart,
      });
    },
    execute: async (args, ctx) => {
      const planId = requireSeatingPlanId(ctx);
      const w = ctx.wedding();
      const capacityRaw = asNumber(args.capacity) ?? 8;
      const capacity = Math.min(30, Math.max(2, Math.round(capacityRaw)));
      const tableCountArg = asNumber(args.tableCount);
      const shape = asEnum(args.shape, ["round", "rectangular"] as const);
      const includeVipTable = asBoolean(args.includeVipTable) ?? true;
      const assignGuests = asBoolean(args.assignGuests) ?? true;
      const keepGroupsTogether = asBoolean(args.keepGroupsTogether) ?? true;
      const leaveSpareSeats = asBoolean(args.leaveSpareSeats) ?? false;

      if (ctx.tables().length > 0) {
        throw new Error(
          "Active plan already has tables. Delete them first or assign guests manually with assign_guest_to_seat.",
        );
      }

      const result = await ctx.operations.seating.setupSeatingPlan(
        ctx.weddingId,
        planId,
        ctx.guests(),
        w.legacySeatingPlanId,
        {
          capacity,
          tableCount: tableCountArg != null ? Math.max(1, Math.round(tableCountArg)) : undefined,
          shape,
          includeVipTable,
          assignGuests,
          keepGroupsTogether,
          leaveSpareSeats,
          labels: {
            vipTable: defaultVipTableName((k: string, p?: Record<string, unknown>) =>
              ctx.translate(k, p),
            ),
            tableName: (index: number) =>
              defaultTableName(
                (k: string, p?: Record<string, unknown>) => ctx.translate(k, p),
                index,
              ),
          },
        },
      );

      const message = !assignGuests
        ? `Created ${result.tablesCreated} tables without guest assignments.`
        : result.unassignedGuestIds.length > 0
          ? `Created ${result.tablesCreated} tables and seated ${result.guestsAssigned} guest units; ${result.unassignedGuestIds.length} could not fit — add tables or increase capacity.`
          : `Created ${result.tablesCreated} tables and seated all ${result.guestsAssigned} guest units.`;

      return {
        ok: true,
        ...result,
        message,
      };
    },
  },
  {
    declaration: {
      name: "assign_guest_to_seat",
      description:
        "Seat a guest at a table. Places the guest's whole party (plus-one and children) using the same rules as the seating canvas. Call list_guests and list_tables first to resolve IDs. Seat numbers are 1-based (seat 1 = first chair). If seatNumber is omitted, picks the first available seat.",
      parameters: Schema.object({
        properties: {
          guestId: Schema.string({ description: "Exact guest ID from list_guests." }),
          tableId: Schema.string({ description: "Exact table ID from list_tables." }),
          seatNumber: Schema.number({
            description:
              "1-based seat number on the target table (optional — auto-picks if omitted).",
          }),
        },
        optionalProperties: ["seatNumber"],
      }),
    },
    destructive: false,
    describe: (args, ctx) => {
      const seat = asNumber(args.seatNumber);
      const seatPart =
        seat != null ? ctx.translate("seating.aiTools.assignGuestSeatPart", { seat }) : "";
      return ctx.translate("seating.aiTools.assignGuest", {
        guest: guestName(ctx, asString(args.guestId) ?? ""),
        table: tableName(ctx, asString(args.tableId) ?? ""),
        seatPart,
      });
    },
    execute: async (args, ctx) => {
      const planId = requireSeatingPlanId(ctx);
      const guestId = requireString(args, "guestId");
      const tableId = requireString(args, "tableId");
      const guest = resolveGuest(ctx, guestId);
      const targetTable = resolveTable(ctx, tableId);
      const allGuests = ctx.guests();
      const byId = guestsById(allGuests);

      if (isGuestShownAsCompanion(guest, allGuests)) {
        throw new Error(
          `Guest "${guestDisplayName(guest)}" is linked as a companion — seat them via their host invitation unit.`,
        );
      }

      const guestTableId = guestTableInPlan(ctx, guest);
      const isSameTable = guestTableId === tableId;

      const seatMap = effectiveSeatMap(targetTable, byId);
      let targetSeatIndex: number;

      const seatNumberArg = asNumber(args.seatNumber);
      if (seatNumberArg != null) {
        targetSeatIndex = seatNumberToIndex(seatNumberArg);
        if (targetSeatIndex >= targetTable.capacity) {
          throw new Error(
            `Seat ${seatNumberArg} is out of range for table "${targetTable.name}" (capacity ${targetTable.capacity}).`,
          );
        }
        if ((targetTable.hiddenSeats ?? []).includes(targetSeatIndex)) {
          throw new Error(`Seat ${seatNumberArg} is deactivated on table "${targetTable.name}".`);
        }
      } else {
        const auto = findFirstEmptySeat(seatMap, guest, targetTable.hiddenSeats ?? []);
        if (auto == null) {
          throw new Error(`No available seat found at table "${targetTable.name}".`);
        }
        targetSeatIndex = auto;
      }

      const occupiedKey = seatMap[targetSeatIndex];
      const displacedGuestId =
        occupiedKey && parseOccupantKey(occupiedKey).guestId !== guestId
          ? parseOccupantKey(occupiedKey).guestId
          : null;
      const workingSeatMap = displacedGuestId
        ? removeGuestFromSeatMap(seatMap, displacedGuestId)
        : seatMap;
      if (!canPlaceParty(workingSeatMap, guest, targetSeatIndex, targetTable.hiddenSeats ?? [])) {
        throw new Error(`Not enough adjacent seats for the whole party at "${targetTable.name}".`);
      }

      const displaced = displacedGuestName(seatMap, targetSeatIndex, guestId, byId);

      const sourceTable = guestTableId
        ? (ctx.tables().find((t) => t.id === guestTableId) ?? null)
        : null;

      if (isSameTable && sourceTable) {
        const sourceSeat = findGuestSeatIndex(seatMap, guestId);
        if (sourceSeat >= 0 && sourceSeat === targetSeatIndex) {
          return {
            ok: true,
            guestId,
            tableId,
            tableName: targetTable.name,
            seatNumber: targetSeatIndex + 1,
            unchanged: true,
          };
        }
        if (sourceSeat >= 0) {
          await ctx.operations.seating.moveGuestWithinTable(
            ctx.weddingId,
            planId,
            guestId,
            guest,
            tableId,
            sourceTable,
            sourceSeat,
            targetSeatIndex,
            byId,
          );
          return {
            ok: true,
            guestId,
            tableId,
            tableName: targetTable.name,
            seatNumber: targetSeatIndex + 1,
            displacedGuest: displaced,
          };
        }
      }

      const allTableIds = ctx.tables().map((t) => t.id);
      await ctx.operations.seating.assignGuestToSeat(
        ctx.weddingId,
        planId,
        guestId,
        guest,
        tableId,
        targetSeatIndex,
        targetTable,
        guestTableId,
        sourceTable,
        byId,
        allGuests,
        allTableIds,
      );

      return {
        ok: true,
        guestId,
        tableId,
        tableName: targetTable.name,
        seatNumber: targetSeatIndex + 1,
        displacedGuest: displaced,
      };
    },
  },
  {
    declaration: {
      name: "move_guest_seat",
      description:
        "Move an already-seated guest to a different seat at the same table. For cross-table moves use assign_guest_to_seat. Seat numbers are 1-based.",
      parameters: Schema.object({
        properties: {
          guestId: Schema.string({ description: "Exact guest ID from list_guests." }),
          seatNumber: Schema.number({
            description: "1-based target seat number on the guest's current table.",
          }),
        },
      }),
    },
    destructive: false,
    describe: (args, ctx) =>
      ctx.translate("seating.aiTools.moveGuest", {
        guest: guestName(ctx, asString(args.guestId) ?? ""),
        seat: asNumber(args.seatNumber) ?? "?",
      }),
    execute: async (args, ctx) => {
      const planId = requireSeatingPlanId(ctx);
      const guestId = requireString(args, "guestId");
      const seatNumber = requireNumber(args, "seatNumber");
      const guest = resolveGuest(ctx, guestId);

      const guestTableId = guestTableInPlan(ctx, guest);
      if (!guestTableId) {
        throw new Error(`Guest is not seated. Use assign_guest_to_seat instead.`);
      }

      const table = resolveTable(ctx, guestTableId);
      const targetSeatIndex = seatNumberToIndex(seatNumber);
      if (targetSeatIndex >= table.capacity) {
        throw new Error(`Seat ${seatNumber} is out of range (capacity ${table.capacity}).`);
      }
      if ((table.hiddenSeats ?? []).includes(targetSeatIndex)) {
        throw new Error(`Seat ${seatNumber} is deactivated on this table.`);
      }

      const byId = guestsById(ctx.guests());
      const seatMap = effectiveSeatMap(table, byId);
      const sourceSeat = findGuestSeatIndex(seatMap, guestId);
      if (sourceSeat < 0) {
        throw new Error(`Could not find guest's current seat on table "${table.name}".`);
      }
      if (sourceSeat === targetSeatIndex) {
        return { ok: true, guestId, seatNumber, unchanged: true };
      }

      const displaced = displacedGuestName(seatMap, targetSeatIndex, guestId, byId);

      await ctx.operations.seating.moveGuestWithinTable(
        ctx.weddingId,
        planId,
        guestId,
        guest,
        table.id,
        table,
        sourceSeat,
        targetSeatIndex,
        byId,
      );

      return {
        ok: true,
        guestId,
        tableId: table.id,
        tableName: table.name,
        seatNumber,
        displacedGuest: displaced,
      };
    },
  },
  {
    declaration: {
      name: "unassign_guest_from_table",
      description:
        "Remove a guest from their table and return them to the unassigned list. Clears the guest's seat map entries.",
      parameters: Schema.object({
        properties: {
          guestId: Schema.string({ description: "Exact guest ID from list_guests." }),
        },
      }),
    },
    destructive: false,
    describe: (args, ctx) =>
      ctx.translate("seating.aiTools.unassignGuest", {
        guest: guestName(ctx, asString(args.guestId) ?? ""),
      }),
    execute: async (args, ctx) => {
      const planId = requireSeatingPlanId(ctx);
      const guestId = requireString(args, "guestId");
      const guest = resolveGuest(ctx, guestId);

      const guestTableId = guestTableInPlan(ctx, guest);
      if (!guestTableId) {
        return { ok: true, guestId, unchanged: true, message: "Guest was not seated." };
      }

      const table = resolveTable(ctx, guestTableId);
      const byId = guestsById(ctx.guests());

      const allTableIds = ctx.tables().map((t) => t.id);
      await ctx.operations.seating.unassignGuestWithSeatMap(
        ctx.weddingId,
        planId,
        guestId,
        table.id,
        table,
        byId,
        allTableIds,
      );

      return { ok: true, guestId, previousTableId: table.id, previousTableName: table.name };
    },
  },
  {
    declaration: {
      name: "create_table",
      description:
        "Add a single seating table to the plan. Position is chosen automatically on the canvas grid. For a full layout with guest assignments on an empty plan, use setup_seating_plan instead.",
      parameters: Schema.object({
        properties: {
          name: Schema.string({
            description: "Table name (optional — defaults to Stół N or Para Młoda for VIP).",
          }),
          shape: Schema.enumString({
            enum: [...TABLE_SHAPES],
            description: "Table shape. Defaults to 'round'.",
          }),
          capacity: Schema.number({ description: "Number of seats (2–30). Defaults to 8." }),
          isVip: Schema.boolean({ description: "Mark as VIP / head table." }),
        },
        optionalProperties: ["name", "shape", "capacity", "isVip"],
      }),
    },
    destructive: false,
    describe: (args, ctx) => {
      const cap = asNumber(args.capacity) ?? 8;
      const name = asString(args.name);
      return ctx.translate("seating.aiTools.createTable", {
        namePart: name ? ctx.translate("seating.aiTools.createTableNamePart", { name }) : "",
        seats: ctx.translate("seating.aiTools.seats", { count: cap }),
      });
    },
    execute: async (args, ctx) => {
      const planId = requireSeatingPlanId(ctx);
      const existingCount = ctx.tables().length;
      const shape = asEnum(args.shape, TABLE_SHAPES) ?? "round";
      const capacityRaw = asNumber(args.capacity) ?? 8;
      const capacity = Math.min(30, Math.max(2, Math.round(capacityRaw)));
      const isVip = asBoolean(args.isVip) ?? false;
      const customName = asString(args.name);
      const t = (k: string, p?: Record<string, unknown>) => ctx.translate(k, p);
      const name =
        customName ?? (isVip ? defaultVipTableName(t) : defaultTableName(t, existingCount + 1));
      const { positionX, positionY } = defaultTablePosition(existingCount, {
        totalTables: existingCount + 1,
        maxCapacity: capacity,
        shape: shape as TableShape,
        includeVipTable: isVip && existingCount === 0,
      });

      const id = await ctx.operations.seating.createTable(ctx.weddingId, planId, {
        name,
        shape: shape as TableShape,
        capacity,
        positionX,
        positionY,
        isVip,
      });

      return { ok: true, tableId: id, name, shape, capacity, isVip, positionX, positionY };
    },
  },
  {
    declaration: {
      name: "update_table",
      description:
        "Update an existing table's name, capacity, shape, VIP flag or notes. Capacity cannot drop below the number of people currently seated.",
      parameters: Schema.object({
        properties: {
          tableId: Schema.string({ description: "Exact table ID from list_tables." }),
          name: Schema.string(),
          capacity: Schema.number({ description: "New seat count (2–30)." }),
          shape: Schema.enumString({ enum: [...TABLE_SHAPES] }),
          isVip: Schema.boolean(),
          notes: Schema.string(),
        },
        optionalProperties: ["name", "capacity", "shape", "isVip", "notes"],
      }),
    },
    destructive: false,
    describe: (args, ctx) =>
      ctx.translate("seating.aiTools.updateTable", {
        table: tableName(ctx, asString(args.tableId) ?? ""),
      }),
    execute: async (args, ctx) => {
      const planId = requireSeatingPlanId(ctx);
      const tableId = requireString(args, "tableId");
      const table = resolveTable(ctx, tableId);
      const byId = guestsById(ctx.guests());
      const filled = tableHeadCount(table, byId);
      const minCapacity = Math.max(2, filled);

      const patch: Record<string, unknown> = {};
      const name = asString(args.name);
      const shape = asEnum(args.shape, TABLE_SHAPES);
      const isVip = asBoolean(args.isVip);
      const notes = asString(args.notes);
      const capacityArg = asNumber(args.capacity);

      if (name !== undefined) patch.name = name;
      if (shape !== undefined) patch.shape = shape;
      if (isVip !== undefined) patch.isVip = isVip;
      if (notes !== undefined) patch.notes = notes;
      if (capacityArg !== undefined) {
        const clamped = Math.min(30, Math.max(minCapacity, Math.round(capacityArg)));
        if (clamped !== capacityArg && capacityArg < minCapacity) {
          throw new Error(
            `Capacity cannot be below ${minCapacity} — that many people are currently seated at "${table.name}".`,
          );
        }
        patch.capacity = clamped;
      }

      if (Object.keys(patch).length === 0) {
        throw new Error("No fields to update were provided.");
      }

      await ctx.operations.seating.updateTable(ctx.weddingId, planId, tableId, patch);
      return { ok: true, tableId, updated: Object.keys(patch) };
    },
  },
  {
    declaration: {
      name: "delete_table",
      description:
        "Permanently delete a table. Seated guests are unassigned first. Requires user confirmation. Call list_tables first.",
      parameters: Schema.object({
        properties: {
          tableId: Schema.string({ description: "Exact table ID from list_tables." }),
        },
      }),
    },
    destructive: true,
    describe: (args, ctx) =>
      ctx.translate("seating.aiTools.deleteTable", {
        table: tableName(ctx, asString(args.tableId) ?? ""),
      }),
    execute: async (args, ctx) => {
      const planId = requireSeatingPlanId(ctx);
      const tableId = requireString(args, "tableId");
      const table = resolveTable(ctx, tableId);

      await ctx.operations.seating.deleteTable(ctx.weddingId, planId, tableId, table.guestIds);

      return {
        ok: true,
        tableId,
        tableName: table.name,
        unassignedGuestCount: table.guestIds.length,
      };
    },
  },
];
